import {AttributeInterpreter, AttributeType, ChoiceValueDecisionState, ConfigurationInterpreter} from '@viamedici-spc/configurator-ts';
import {attributeIdToString, stringToAttributeId} from '../components/spc/util/spcUtils';
import {conditional, conditionals} from './conditional';

export function hasFeatureValue(spcAttributeKey, valueId, spcConfiguration) {
	if (!spcConfiguration) return false;
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	const spcAttribute = ConfigurationInterpreter.getChoiceAttribute(spcConfiguration, spcAttributeKey);
	if (spcAttribute) {
		const includedChoiceValues = AttributeInterpreter.getIncludedChoiceValues(spcAttribute).map(v => v.id);
		return includedChoiceValues.includes(valueId);
	} else {
		return false;
	}
}

export function hasNoFeatureValue(spcAttributeKey, valueKey, spcAttributes) {
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	return !this.hasFeatureValue(spcAttributeKey, valueKey, spcAttributes);
}
export function hasNoFeatureValues(spcAttributeKey, spcAttributes, spcConfiguration) {
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	const spcAttribute = ConfigurationInterpreter.getChoiceAttribute(spcConfiguration, spcAttributeKey);

	const values = AttributeInterpreter.getAllowedChoiceValues(spcAttribute).map(v => v.id);
	console.log('hasNoFeatureValues', spcAttributeKey, spcAttributes, spcAttribute, values);
	return !values.length > 0;
}
export function hasOnlyThisFeatureValue(spcAttributeKey, valueId, spcConfiguration) {
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	const spcAttribute = ConfigurationInterpreter.getChoiceAttribute(spcConfiguration, spcAttributeKey);

	if (spcAttribute) {
		const includedChoiceValues = AttributeInterpreter.getIncludedChoiceValues(spcAttribute).map(v => v.id);
		// return true when includedChoiceValues only contains valueId and no other values
		if (includedChoiceValues.length === 1 && includedChoiceValues[0] === valueId) {
			return true;
		}
	}
	return false;
}

export function getFeature(spcAttributeKey, spcAttributes) {
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	return spcAttributes?.find(attr => attr.key === spcAttributeKey);
}

export function allSatisfied(spcAttributeKey, spcConfiguration) {
	return spcConfiguration.isSatisfied;
}

export function isSatisfied(spcAttributeKey, spcAttributes) {
	// console.log('isSatisfied', spcAttributes, spcAttributeKey);
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	return spcAttributes?.find(attr => attr.key === spcAttributeKey)?.isSatisfied;
}

export function isDecided(spcAttributeKey, spcAttributes) {
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	return spcAttributes?.find(attr => attr.key === spcAttributeKey).isSatisfied;
}

export function getValueOfFeature(spcAttributeKey, spcConfiguration) {
	if (!spcConfiguration) return null;
	if (!spcAttributeKey.includes('::')) {
		spcAttributeKey = 'root::' + spcAttributeKey;
	} // complete simple key
	const spcAttribute = ConfigurationInterpreter.getChoiceAttribute(spcConfiguration, spcAttributeKey);

	let includedChoiceValues = [];
	try {
		includedChoiceValues = AttributeInterpreter.getIncludedChoiceValues(spcAttribute);
	} catch (e) {
		//console.log('getValueOfFeature attribute not available', spcAttributeKey);
		return null;
	}
	// console.log('getValueOfFeature', spcAttributeKey, spcConfiguration, spcAttribute, includedChoiceValues);
	// gets first value
	// TODO: handle multiValue Features
	return includedChoiceValues.length > 0 ? includedChoiceValues[0].id : null;
}

export function getDimmingLevelSteps(spcConfiguration, conditionalVariantValues) {
	if (!spcConfiguration) return [];

	// gets all available value from model
	// only included Values, assumed to be filtered by spc
	// major dimming levels are id of attribute values
	let values = ConfigurationInterpreter.getChoiceAttribute(spcConfiguration, 'root::LE_DimmingLevel_1').values;
	values = Array.from(values)
		.filter(v => v[1].possibleDecisionStates.includes(ChoiceValueDecisionState.Included))
		.map(v => v[1])
		.map(v => parseInt(v.id, 10));

	const stepsString = conditional(spcConfiguration, conditionalVariantValues, 'EB_DimmingSteps', 'EditorialTable', 1);

	let steps = [];
	if (stepsString) {
		steps = stepsString
			.split(';').map(step => parseInt(step, 10))
			.sort((a, b) => b.value - a.value)
			.map(step => ({
				value: step,
				type: values.includes(step) ? 'major' : 'intermediate'
			}));
	}
	return steps;
}

// map context attribut to api Context (shorter, and can be changed globally)
const contextMap = {
	SC: 'SystemComponent',
	F: 'Feature',
	'F.S': 'Feature.ShortText',
	'F.L': 'Feature.LongText',
	'F.D': 'Feature.DescriptionUx',
	FV: 'FeatureValue',
	'FV.S': 'FeatureValue.ShortText',
	'FV.L': 'FeatureValue.LongText',
	'FV.D': 'FeatureValue.DescriptionUx',
	D: 'Description',
	'D.S': 'Description.ShortText',
	'D.L': 'Description.LongText',
	'D.D': 'Description.DescriptionUx',
	DV: 'DescriptionValue',
	'DV.S': 'DescriptionValue.ShortText',
	'DV.L': 'DescriptionValue.LongText',
	'DV.D': 'DescriptionValue.DescriptionUx',
};

export function pimText(lang, featureId, context, spcConfiguration, translations, valueId = null, prefix = '', suffix = '', defaultString = '', showIdWhenMissing = false) {
	if ((context.startsWith('FV') && valueId === null && !spcConfiguration) || !translations) return '';

	let lookup = lang + '.' + contextMap[context] + '.' + featureId;
	if (context.startsWith('FV')) {
		if (valueId !== null) {
			lookup += '.' + valueId;
		} else {
			lookup += '.' + getValueOfFeature(featureId, spcConfiguration);
		}
	}
	if (translations[lookup] === undefined) {
		return showIdWhenMissing ? (valueId ? ('[' + valueId + ']') : ('[' + lookup + ']')) : '';
	}
	const translation = translations[lookup];
	return translation ? (prefix + translation.text + suffix) : (showIdWhenMissing ? '[' + lookup + ']' : '');
}

export function pimTextDebug(lang, featureId, context, spcConfiguration, translations, valueId = null, prefix = '', suffix = '', defaultString = '') {
	if ((context.startsWith('FV') && valueId === null && !spcConfiguration) || translations.length === 0) return '';

	let lookup = lang + '.' + contextMap[context] + '.' + featureId;
	if (context.startsWith('FV')) {
		if (valueId !== null) {
			lookup += '.' + valueId;
		} else {
			lookup += '.' + getValueOfFeature(featureId, spcConfiguration);
		}
	}
	return '[' + lookup + ']';
}

export function hasPimText(lang, featureId, context, spcConfiguration, translations, valueId = null, prefix = '', suffix = '') {
	if (!spcConfiguration || translations.length === 0) return '';

	let lookup = lang + '.' + contextMap[context] + '.' + featureId;
	if (context.startsWith('FV')) {
		if (valueId !== null) {
			lookup += '.' + valueId;
		} else {
			lookup += '.' + getValueOfFeature(featureId, spcConfiguration);
		}
	}
	return translations[lookup] !== undefined && translations[lookup] !== '';
}

export function spcExpliciteChoiceDecision(attributeKey, valueId) {
	return {
		type: AttributeType.Choice,
		attributeId: stringToAttributeId(attributeKey),
		choiceValueId: valueId,
		state: ChoiceValueDecisionState.Included,
	};
}
